import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, Button, Container, Group, Stack, Text, Title, Paper } from "@mantine/core";
import { AlertCircle, Refresh } from "tabler-icons-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  companyId?: string;
  userEmail?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    const companyTag = this.props.companyId ? ` [Company: ${this.props.companyId}]` : "";
    const userTag = this.props.userEmail ? ` [User: ${this.props.userEmail}]` : "";
    const route = typeof window !== "undefined" ? window.location.pathname : "unknown";

    // Structured error log format for client-side observability
    console.error(
      `[Action: UI Render]${companyTag}${userTag} [Route: ${route}] Client render crash: ${error.message}`,
      {
        error,
        componentStack: errorInfo.componentStack,
        route,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      }
    );
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Container size="sm" py={50}>
          <Paper p="xl" withBorder shadow="sm" radius="md">
            <Stack spacing="md">
              <Group>
                <AlertCircle size={32} color="#fa5252" />
                <Title order={3}>Something went wrong</Title>
              </Group>

              <Text size="sm" color="dimmed">
                An unexpected error occurred while rendering this page. Our team has been notified.
              </Text>

              <Alert
                icon={<AlertCircle size={16} />}
                title="Error Details"
                color="red"
                variant="light"
              >
                <Text size="xs" style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                  {this.state.error?.message || "Unknown error occurred"}
                </Text>
              </Alert>

              <Group position="right" mt="md">
                <Button variant="default" onClick={this.handleReset}>
                  Try Again
                </Button>
                <Button
                  leftIcon={<Refresh size={16} />}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}
